import { useState, useEffect } from "react";
import { analyzeGrammar, getGrammarExercise, listTexts, getSampleTexts } from "../lib/api";
import { BookOpen, Sparkles, ArrowLeft, Check, X, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LEVELS = [
  { id: "simple", label: "Simplifie", desc: "nom, verbe, adjectif, petit mot" },
  { id: "intermediate", label: "Intermediaire", desc: "+ determinant, pronom, preposition" },
  { id: "complete", label: "Complet", desc: "Analyse grammaticale complete" },
];

export default function GrammarPage() {
  const navigate = useNavigate();
  const [texts, setTexts] = useState([]);
  const [samples, setSamples] = useState([]);
  const [selectedText, setSelectedText] = useState(null);
  const [level, setLevel] = useState("intermediate");
  const [analysis, setAnalysis] = useState(null);
  const [exercise, setExercise] = useState(null);
  const [step, setStep] = useState("select"); // select | analyze | exercise
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("nature"); // nature | function | structure
  const [exerciseAnswers, setExerciseAnswers] = useState({});
  const [exerciseChecked, setExerciseChecked] = useState(false);

  useEffect(() => {
    Promise.all([
      listTexts().catch(() => ({ data: [] })),
      getSampleTexts().catch(() => ({ data: [] })),
    ]).then(([t, s]) => {
      setTexts(t.data);
      setSamples(s.data);
    });
  }, []);

  const handleAnalyze = async () => {
    if (!selectedText) return;
    setLoading(true);
    try {
      const text = selectedText.text || selectedText.raw_text;
      const res = await analyzeGrammar(text, level);
      setAnalysis(res.data);
      setStep("analyze");
    } catch (err) {
      console.error("Analysis failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartExercise = async (type) => {
    if (!selectedText) return;
    setLoading(true);
    try {
      const text = selectedText.text || selectedText.raw_text;
      const res = await getGrammarExercise(text, type, level);
      setExercise(res.data);
      setExerciseAnswers({});
      setExerciseChecked(false);
      setStep("exercise");
    } catch (err) {
      console.error("Exercise failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckExercise = () => {
    setExerciseChecked(true);
  };

  const getScore = () => {
    if (!exercise || !exerciseChecked) return null;
    const questions = exercise.questions || [];
    let correct = 0;
    questions.forEach((q, i) => {
      if (exerciseAnswers[i] === q.answer) correct++;
    });
    return { correct, total: questions.length };
  };

  return (
    <div data-testid="grammar-page" className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8 page-enter has-bottom-nav lg:pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        {step !== "select" && (
          <button
            data-testid="grammar-back"
            onClick={() => {
              setStep("select");
              setAnalysis(null);
              setExercise(null);
            }}
            className="p-2 rounded-xl text-slate-400 active:text-slate-700 active:bg-slate-100 touch-target"
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <BookOpen size={24} className="text-purple-500" />
        <h1 data-testid="grammar-title" className="font-fredoka text-3xl md:text-4xl font-bold text-slate-800">
          {step === "select" ? "Grammaire" : step === "analyze" ? "Analyse" : "Exercice"}
        </h1>
      </div>

      {/* Step 1: Select text */}
      {step === "select" && (
        <div className="space-y-6 page-enter">
          <p className="font-nunito text-base text-slate-500">
            Choisis un texte pour analyser sa grammaire ou faire des exercices !
          </p>

          {/* Level selector */}
          <div className="bg-white rounded-2xl border-2 border-purple-100 p-4">
            <h3 className="font-fredoka font-bold text-base text-slate-700 mb-3">
              Niveau d'analyse
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {LEVELS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`level-${l.id}`}
                  onClick={() => setLevel(l.id)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    level === l.id
                      ? "bg-purple-500 text-white shadow-[0_4px_0_rgb(126,34,206)]"
                      : "bg-purple-50 text-purple-700 active:bg-purple-100"
                  }`}
                >
                  <p className="font-fredoka font-bold text-sm">{l.label}</p>
                  <p className="font-nunito text-xs mt-1 opacity-80">{l.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Sample texts */}
          <div className="space-y-2">
            <h3 className="font-fredoka font-bold text-lg text-slate-700">Textes exemples</h3>
            {samples.map((s, i) => (
              <button
                key={i}
                data-testid={`grammar-sample-${i}`}
                onClick={() => setSelectedText({ ...s, id: `sample-${i}` })}
                className={`w-full text-left bg-white border-2 rounded-2xl p-4 transition-all touch-target ${
                  selectedText?.id === `sample-${i}`
                    ? "border-purple-400 bg-purple-50"
                    : "border-slate-100 active:border-purple-300"
                }`}
              >
                <p className="font-fredoka font-bold text-base text-slate-800">{s.title}</p>
                <p className="font-nunito text-sm text-slate-400 mt-1 line-clamp-1">{s.text}</p>
              </button>
            ))}
          </div>

          {texts.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-fredoka font-bold text-lg text-slate-700">Mes textes</h3>
              {texts.map((t) => (
                <button
                  key={t.id}
                  data-testid={`grammar-text-${t.id}`}
                  onClick={() => setSelectedText(t)}
                  className={`w-full text-left bg-white border-2 rounded-2xl p-4 transition-all touch-target ${
                    selectedText?.id === t.id
                      ? "border-purple-400 bg-purple-50"
                      : "border-slate-100 active:border-purple-300"
                  }`}
                >
                  <p className="font-fredoka font-bold text-base text-slate-800">{t.title}</p>
                  <p className="font-nunito text-sm text-slate-400 mt-1 line-clamp-1">{t.raw_text}</p>
                </button>
              ))}
            </div>
          )}

          {/* Action buttons */}
          {selectedText && (
            <div className="space-y-3 pt-4">
              <button
                data-testid="analyze-btn"
                onClick={handleAnalyze}
                disabled={loading}
                className="w-full py-4 bg-purple-500 active:bg-purple-600 text-white font-fredoka font-bold text-lg rounded-full shadow-[0_4px_0_rgb(126,34,206)] btn-bouncy touch-target disabled:opacity-50"
              >
                <Sparkles size={20} className="inline mr-2" />
                Analyser la grammaire
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="exercise-pos-btn"
                  onClick={() => handleStartExercise("identify_pos")}
                  disabled={loading}
                  className="py-3 bg-blue-500 active:bg-blue-600 text-white font-fredoka font-bold text-sm rounded-full shadow-[0_4px_0_rgb(29,78,216)] btn-bouncy touch-target disabled:opacity-50"
                >
                  Nature des mots
                </button>
                <button
                  data-testid="exercise-func-btn"
                  onClick={() => handleStartExercise("identify_function")}
                  disabled={loading}
                  className="py-3 bg-green-500 active:bg-green-600 text-white font-fredoka font-bold text-sm rounded-full shadow-[0_4px_0_rgb(22,163,74)] btn-bouncy touch-target disabled:opacity-50"
                >
                  Fonctions
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Analysis view */}
      {step === "analyze" && analysis && (
        <div className="space-y-4 page-enter">
          {/* View mode toggle */}
          <div className="flex gap-2 bg-slate-100 p-1 rounded-xl">
            {[
              { id: "nature", label: "Nature" },
              { id: "function", label: "Fonction" },
              { id: "structure", label: "Structure" },
            ].map((m) => (
              <button
                key={m.id}
                data-testid={`view-${m.id}`}
                onClick={() => setViewMode(m.id)}
                className={`flex-1 py-2 px-3 rounded-lg font-nunito font-bold text-sm transition-all ${
                  viewMode === m.id
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Analysis display */}
          {analysis.sentences.map((sent, si) => (
            <div
              key={si}
              data-testid={`sentence-${si}`}
              className="bg-white rounded-2xl border-2 border-slate-100 p-5 shadow-md"
            >
              {/* Token display */}
              <div className="flex flex-wrap gap-1 mb-4">
                {sent.tokens.map((token, ti) =>
                  token.is_space ? (
                    <span key={ti} className="w-2" />
                  ) : token.is_punct ? (
                    <span key={ti} className="font-lexend text-lg text-slate-400">
                      {token.text}
                    </span>
                  ) : (
                    <span
                      key={ti}
                      data-testid={`token-${si}-${ti}`}
                      className="relative group"
                    >
                      <span
                        className="font-lexend text-lg px-1 py-0.5 rounded"
                        style={{
                          backgroundColor:
                            viewMode === "nature"
                              ? `${token.pos_color}20`
                              : viewMode === "function"
                              ? `${token.dep_color}20`
                              : "transparent",
                          color:
                            viewMode === "nature"
                              ? token.pos_color
                              : viewMode === "function"
                              ? token.dep_color
                              : "#1E293B",
                          borderBottom:
                            viewMode !== "structure"
                              ? `2px solid ${
                                  viewMode === "nature"
                                    ? token.pos_color
                                    : token.dep_color
                                }`
                              : "none",
                        }}
                      >
                        {token.text}
                      </span>
                      {/* Tooltip */}
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                        {viewMode === "nature"
                          ? token.pos_label
                          : token.dep_label}
                      </span>
                    </span>
                  )
                )}
              </div>

              {/* Structure view */}
              {viewMode === "structure" && sent.structure.groups.length > 0 && (
                <div className="space-y-2 pt-3 border-t border-slate-100">
                  <p className="font-nunito font-bold text-xs text-slate-400 uppercase">
                    Structure de la phrase
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {sent.structure.groups.map((group, gi) => (
                      <div
                        key={gi}
                        className="px-3 py-2 rounded-xl border-2"
                        style={{
                          borderColor: group.color,
                          backgroundColor: `${group.color}10`,
                        }}
                      >
                        <p
                          className="font-fredoka font-bold text-xs mb-1"
                          style={{ color: group.color }}
                        >
                          {group.label}
                        </p>
                        <p className="font-nunito text-sm text-slate-700">
                          {group.tokens.join(" ")}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Legend */}
          <div className="bg-slate-50 rounded-2xl p-4">
            <p className="font-nunito font-bold text-xs text-slate-400 uppercase mb-2">
              Legende ({viewMode === "nature" ? "Nature" : viewMode === "function" ? "Fonction" : "Structure"})
            </p>
            <div className="flex flex-wrap gap-2">
              {viewMode === "nature" &&
                Object.entries(analysis.stats).map(([label, count]) => (
                  <span
                    key={label}
                    className="px-2 py-1 rounded text-xs font-nunito font-bold"
                    style={{
                      backgroundColor: `${getColorForLabel(label, "nature")}20`,
                      color: getColorForLabel(label, "nature"),
                    }}
                  >
                    {label} ({count})
                  </span>
                ))}
              {viewMode === "function" && (
                <>
                  {["sujet", "verbe principal", "COD", "COI", "complément"].map((f) => (
                    <span
                      key={f}
                      className="px-2 py-1 rounded text-xs font-nunito font-bold"
                      style={{
                        backgroundColor: `${getColorForLabel(f, "function")}20`,
                        color: getColorForLabel(f, "function"),
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </>
              )}
              {viewMode === "structure" && (
                <>
                  {["GNS", "GV", "COD", "CC"].map((g, i) => (
                    <span
                      key={g}
                      className="px-2 py-1 rounded text-xs font-nunito font-bold"
                      style={{
                        backgroundColor: `${["#3B82F6", "#EF4444", "#22C55E", "#F97316"][i]}20`,
                        color: ["#3B82F6", "#EF4444", "#22C55E", "#F97316"][i],
                      }}
                    >
                      {g}
                    </span>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Exercise view */}
      {step === "exercise" && exercise && (
        <div className="space-y-4 page-enter">
          <div className="bg-purple-50 rounded-2xl p-4 border-2 border-purple-200">
            <h3 className="font-fredoka font-bold text-lg text-purple-800">
              {exercise.title}
            </h3>
            <p className="font-nunito text-sm text-purple-600 mt-1">
              {exercise.instructions}
            </p>
          </div>

          {/* Questions */}
          {(exercise.questions || []).map((q, i) => (
            <div
              key={i}
              data-testid={`question-${i}`}
              className={`bg-white rounded-2xl border-2 p-4 shadow-md transition-all ${
                exerciseChecked
                  ? exerciseAnswers[i] === q.answer
                    ? "border-green-400 bg-green-50"
                    : "border-red-400 bg-red-50"
                  : "border-slate-100"
              }`}
            >
              <p className="font-nunito text-sm text-slate-500 mb-2">
                {q.sentence}
              </p>
              <p className="font-fredoka font-bold text-xl text-slate-800 mb-3">
                <span
                  className="px-2 py-1 rounded"
                  style={{ backgroundColor: `${q.color}20`, color: q.color }}
                >
                  {q.word}
                </span>
              </p>

              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() =>
                      !exerciseChecked &&
                      setExerciseAnswers({ ...exerciseAnswers, [i]: opt })
                    }
                    disabled={exerciseChecked}
                    className={`px-3 py-2 rounded-xl font-nunito font-bold text-sm transition-all ${
                      exerciseAnswers[i] === opt
                        ? exerciseChecked
                          ? opt === q.answer
                            ? "bg-green-500 text-white"
                            : "bg-red-500 text-white"
                          : "bg-purple-500 text-white"
                        : exerciseChecked && opt === q.answer
                        ? "bg-green-100 text-green-700 border-2 border-green-400"
                        : "bg-slate-100 text-slate-600 active:bg-slate-200"
                    }`}
                  >
                    {opt}
                    {exerciseChecked && opt === q.answer && (
                      <Check size={14} className="inline ml-1" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Score & actions */}
          {exerciseChecked && getScore() && (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center shadow-md">
              <img
                src="/LEcoleDeNavi4.png"
                alt="Navi"
                className="h-20 mx-auto mb-3 mascot-float"
              />
              <p
                className="font-fredoka font-bold text-4xl mb-1"
                style={{
                  color:
                    getScore().correct / getScore().total >= 0.8
                      ? "#22C55E"
                      : getScore().correct / getScore().total >= 0.5
                      ? "#F97316"
                      : "#EF4444",
                }}
              >
                {Math.round((getScore().correct / getScore().total) * 100)}%
              </p>
              <p className="font-nunito text-base text-slate-500">
                {getScore().correct}/{getScore().total} reponses correctes
              </p>
            </div>
          )}

          {!exerciseChecked && (
            <button
              data-testid="check-exercise-btn"
              onClick={handleCheckExercise}
              disabled={Object.keys(exerciseAnswers).length < (exercise.questions?.length || 0)}
              className="w-full py-4 bg-purple-500 active:bg-purple-600 text-white font-fredoka font-bold text-lg rounded-full shadow-[0_4px_0_rgb(126,34,206)] btn-bouncy touch-target disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check size={20} className="inline mr-2" />
              Verifier mes reponses
            </button>
          )}

          {exerciseChecked && (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setExerciseAnswers({});
                  setExerciseChecked(false);
                }}
                className="flex-1 py-3 bg-blue-500 active:bg-blue-600 text-white font-fredoka font-bold rounded-full shadow-[0_4px_0_rgb(29,78,216)] btn-bouncy touch-target"
              >
                Recommencer
              </button>
              <button
                onClick={() => {
                  setStep("select");
                  setExercise(null);
                }}
                className="flex-1 py-3 bg-orange-500 active:bg-orange-600 text-white font-fredoka font-bold rounded-full shadow-[0_4px_0_rgb(194,65,12)] btn-bouncy touch-target"
              >
                Autre texte
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper function for colors
function getColorForLabel(label, mode) {
  const colors = {
    nature: {
      nom: "#3B82F6",
      "nom propre": "#8B5CF6",
      verbe: "#EF4444",
      auxiliaire: "#F97316",
      adjectif: "#22C55E",
      adverbe: "#14B8A6",
      déterminant: "#6B7280",
      pronom: "#EC4899",
      préposition: "#F59E0B",
      conjonction: "#6366F1",
      "petit mot": "#9CA3AF",
      ponctuation: "#9CA3AF",
      nombre: "#0EA5E9",
    },
    function: {
      sujet: "#3B82F6",
      "verbe principal": "#EF4444",
      COD: "#22C55E",
      COI: "#14B8A6",
      complément: "#F97316",
    },
  };
  return colors[mode]?.[label] || "#D1D5DB";
}
